import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { SearchableSelect } from '@/components/custom/forms/SearchableSelect';
import type { SearchableSelectOption } from '@/components/custom/forms/SearchableSelect';
import { STATUS_OPTIONS } from '@cms/utils';
import { FilterField } from './FilterField';

export interface StatusFilterProps {
  value: string;
  onChange: (value: string) => void;
  /** Override options (e.g. for Escrow/Supplier with extra statuses). Defaults to STATUS_OPTIONS. */
  options?: ReadonlyArray<{ value: string; label: string }>;
  placeholder?: string;
  emptyOption?: { value: ''; label: string };
  className?: string;
  id?: string;
  disabled?: boolean;
  /** When set, renders a label above the control (e.g. "Status"). */
  label?: string;
}

export function StatusFilter({
  value,
  onChange,
  options: optionsProp,
  placeholder: placeholderProp,
  emptyOption: emptyOptionProp,
  className = '',
  id,
  disabled = false,
  label: labelProp,
}: Readonly<StatusFilterProps>) {
  const { t } = useTranslation('admin');
  const placeholder = placeholderProp ?? t('shared.filters.search_status');
  const emptyOption = emptyOptionProp ?? { value: '' as const, label: t('shared.status.all') };

  const options: SearchableSelectOption[] = useMemo(
    () =>
      (optionsProp ?? STATUS_OPTIONS).map(opt => ({
        value: opt.value,
        label: optionsProp ? opt.label : t(`shared.status.${opt.value}`),
      })),
    [optionsProp, t],
  );

  const control = (
    <SearchableSelect
      id={id}
      options={options}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      emptyOption={emptyOption}
      className={className}
      disabled={disabled}
    />
  );

  const wrapperClass = 'min-w-[140px] shrink-0'; /* fixed width in filter row */
  return (
    <div className={[wrapperClass, className].filter(Boolean).join(' ')}>
      <FilterField label={labelProp} id={id}>
        {control}
      </FilterField>
    </div>
  );
}
